/**
 * MergeApprovalDialog — shown when a merge returns 409 "approvals_required".
 *
 * Displays pending owner approvals, allows approvers to act,
 * and auto-refreshes until all are resolved.
 */

import { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Callout,
  Dialog,
  Flex,
  ScrollArea,
  Text,
  TextArea,
} from "@radix-ui/themes";

import {
  useMergeApprovals,
  useApproveChange,
  useRejectChange,
  useWaiveApproval,
} from "../../hooks/useMergeApprovals";

interface PendingApproval {
  id: string;
  node_path: string;
  old_value: number | null;
  new_value: number | null;
  old_units: string | null;
  new_units: string | null;
  required_approver: string;
  status: string;
  review_comment?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  sourceBranch: string;
  targetBranch: string;
  currentUserId: string;
  isProjectOwner: boolean;
  initialApprovals?: PendingApproval[];
  onRetryMerge: () => void;
}

const STATUS_COLORS: Record<string, "orange" | "green" | "red" | "blue" | "gray"> = {
  pending: "orange",
  approved: "green",
  rejected: "red",
  waived: "blue",
};

export function MergeApprovalDialog({
  open,
  onOpenChange,
  projectId,
  sourceBranch,
  targetBranch,
  currentUserId,
  isProjectOwner,
  onRetryMerge,
}: Props) {
  const { data: approvals = [] } = useMergeApprovals(
    projectId,
    sourceBranch,
    targetBranch,
    open,
  );
  const approveMut = useApproveChange(projectId, sourceBranch, targetBranch);
  const rejectMut = useRejectChange(projectId, sourceBranch, targetBranch);
  const waiveMut = useWaiveApproval(projectId, sourceBranch, targetBranch);

  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState("");

  const pendingApprovals = approvals.filter((a) => a.status === "pending");
  const allResolved = pendingApprovals.length === 0;
  const hasRejected = approvals.some((a) => a.status === "rejected");

  function handleReject(approvalId: string) {
    rejectMut.mutate(
      { approvalId, comment: rejectComment },
      {
        onSuccess: () => {
          setRejectingId(null);
          setRejectComment("");
        },
      },
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="650px">
        <Dialog.Title>Owner Approval Required</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Changes to owned reference quantities require approval before merging{" "}
          <Text color="orange" weight="medium">{sourceBranch}</Text> into{" "}
          <Text color="orange" weight="medium">{targetBranch}</Text>.
        </Dialog.Description>

        <Box mt="3">
          {allResolved && !hasRejected && (
            <Callout.Root color="green" mb="3">
              <Callout.Text>All approvals resolved. You can proceed with the merge.</Callout.Text>
            </Callout.Root>
          )}

          {hasRejected && (
            <Callout.Root color="red" mb="3">
              <Callout.Text>
                Some changes were rejected. Review the reasons below before re-submitting.
              </Callout.Text>
            </Callout.Root>
          )}

          <ScrollArea style={{ maxHeight: 350 }}>
            <Box asChild style={{ width: "100%", borderCollapse: "collapse" }}>
              <table>
                <thead>
                  <tr>
                    {["Parameter", "Change", "Approver", "Status", "Actions"].map(
                      (h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: "left",
                            padding: "6px 8px",
                            borderBottom: "1px solid var(--gray-a4)",
                          }}
                        >
                          <Text size="1" weight="medium">{h}</Text>
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {approvals.map((a) => (
                    <tr key={a.id}>
                      <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                        <Text
                          size="1"
                          style={{ fontFamily: "var(--code-font-family, monospace)" }}
                        >
                          {a.node_path.split(".").pop()}
                        </Text>
                      </td>
                      <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                        <Text size="1">
                          {a.old_value ?? "—"} → {a.new_value ?? "—"}
                        </Text>
                      </td>
                      <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                        <Text size="1">{a.required_approver}</Text>
                      </td>
                      <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                        <Badge size="1" color={STATUS_COLORS[a.status] ?? "gray"}>
                          {a.status}
                        </Badge>
                      </td>
                      <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                        {a.status === "pending" && (
                          <Flex gap="1">
                            {currentUserId === a.required_approver && (
                              <>
                                <Button
                                  size="1"
                                  color="green"
                                  variant="soft"
                                  onClick={() => approveMut.mutate(a.id)}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="1"
                                  color="red"
                                  variant="soft"
                                  onClick={() => setRejectingId(a.id)}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                            {isProjectOwner &&
                              currentUserId !== a.required_approver && (
                                <Button
                                  size="1"
                                  color="blue"
                                  variant="soft"
                                  onClick={() => waiveMut.mutate(a.id)}
                                >
                                  Waive
                                </Button>
                              )}
                          </Flex>
                        )}
                        {a.status === "rejected" && a.review_comment && (
                          <Text size="1" color="red">
                            {a.review_comment}
                          </Text>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </ScrollArea>

          {rejectingId && (
            <Box
              mt="3"
              p="3"
              style={{
                border: "1px solid var(--red-a4)",
                borderRadius: "var(--radius-2)",
              }}
            >
              <Text size="2" weight="medium" color="red" mb="2">
                Rejection reason (required)
              </Text>
              <TextArea
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="Explain why this change should not be merged..."
                rows={3}
              />
              <Flex gap="2" justify="end" mt="2">
                <Button
                  variant="soft"
                  color="gray"
                  size="1"
                  onClick={() => {
                    setRejectingId(null);
                    setRejectComment("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  color="red"
                  size="1"
                  disabled={!rejectComment.trim()}
                  onClick={() => handleReject(rejectingId)}
                >
                  Confirm Reject
                </Button>
              </Flex>
            </Box>
          )}
        </Box>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Close
            </Button>
          </Dialog.Close>
          {allResolved && !hasRejected && (
            <Button onClick={onRetryMerge}>Retry Merge</Button>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
